import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import {Tabs} from '@jetbrains/ring-ui/components/tabs/tabs'
import {memo, Suspense, useCallback, useEffect, useMemo} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import BranchFilter from '../../../containers/BranchFilter'
import {useAppSelector} from '../../../hooks/react-redux'
import useDocumentTitle from '../../../hooks/useDocumentTitle'
import {PlaceIdList} from '../../../plugins/plugins.types'
import {Policy} from '../../../rest/branches'
import {getHrefWithQueryParams} from '../../../routes'
import type {Branch} from '../../../services/rest'
import {restApi} from '../../../services/rest'
import {getProjectBuildTypeFilter, stringifyId} from '../../../types'
import {BS} from '../../../types/BS_types'
import {stringifyBranch} from '../../../utils/branchNames'
import {emptyArray} from '../../../utils/empty'
import {subscribeOnProjectEvents} from '../../../utils/subscriber'
import {TEST_MUTE_UPDATED, TEST_RESPONSIBILITY_CHANGED} from '../../../utils/subscriptionEvents'
import CopyIconButton from '../../common/CopyIconButton/CopyIconButton'
import ErrorPage from '../../common/ErrorPage/ErrorPage'
import Loader from '../../common/Loader/Loader'
import OverviewTab from '../../common/OverviewTab/OverviewTab'
import Plugin from '../../common/Plugin/Plugin'
import InvestigationHistory from '../../packages/InvestigationHistory/InvestigationHistoryList/InvestigationHistoryList'
import InvestigationMuteButtons from '../../packages/Tests/InvestigationMuteButtons/InvestigationMuteButtons'
import {getTestOccurenceArg} from '../../packages/Tests/Tests.rest'
import {getTestOccurrence} from '../../packages/Tests/Tests.selectors'
import {trimAndRemoveColon} from '../../packages/Tests/Tests.utils'

import TestOverviewTab from './OverviewTab/OverviewTab'
import ProjectsSelect from './ProjectsSelect/ProjectsSelect'
import {tabs, TestPageTabNamesEnum} from './TestHistoryPage.tabs'
import type {Props, TestTab} from './TestHistoryPage.types'

import styles from './TestHistoryPage.css'

function TestHistoryPage({
  testId,
  buildTypeId,
  projectId,
  projectInternalId,
  branch,
  projectName,
  buildTypeName,
  currentTab,
}: Props) {
  const locator = `test:(id:${stringifyId(testId)}),affectedProject:${stringifyId(
    projectId,
  )},includePersonal:true`
  const navigate = useNavigate()
  const location = useLocation()

  const {refetch, testOccurrenceId, isReady} =
    restApi.endpoints.getTestOccurrenceNormalized.useQuery(
      getTestOccurenceArg(locator, {
        withMuteInfo: true,
        withInvestigationInfo: true,
      }),
      {
        selectFromResult: ({data}) => ({
          testOccurrenceId: data?.result[0],
          isReady: data != null,
        }),
      },
    )
  const testName = useAppSelector(state => getTestOccurrence(state, testOccurrenceId)?.name)
  const testClass = useAppSelector(
    state => getTestOccurrence(state, testOccurrenceId)?.test?.parsedTestName?.testClass,
  )
  const testNameWithParameters = useAppSelector(
    state =>
      getTestOccurrence(state, testOccurrenceId)?.test?.parsedTestName?.testNameWithParameters,
  )
  const testSuite = useAppSelector(
    state => getTestOccurrence(state, testOccurrenceId)?.test?.parsedTestName?.testSuite,
  )
  const testPackage = useAppSelector(
    state => getTestOccurrence(state, testOccurrenceId)?.test?.parsedTestName?.testPackage,
  )

  const fullTestName =
    [testPackage, testClass, testNameWithParameters].filter(Boolean).join('.') || testName

  const entityName = projectName ?? buildTypeName
  useDocumentTitle(
    fullTestName != null && entityName != null
      ? `${entityName} > Test history of ${fullTestName} — TeamCity`
      : 'TeamCity Loading...',
  )

  const testOccurrenceIds = useMemo(
    () => (testOccurrenceId != null ? [testOccurrenceId] : emptyArray),
    [testOccurrenceId],
  )
  const onSelectBranch = useCallback(
    (selected: Branch | null | undefined, replace?: boolean) => {
      const selectedBranch = stringifyBranch(selected)

      if (projectId != null && BS?.Branch) {
        BS.Branch.saveBranch(projectId, selectedBranch)
      }

      navigate(
        getHrefWithQueryParams(location, null, oldParams => ({
          ...oldParams,
          branch: selectedBranch,
          page: null,
        })),
        replace != null ? {replace} : {},
      )
    },
    [navigate, location, projectId],
  )

  const getTabLink = useCallback(
    (tabId: string) =>
      getHrefWithQueryParams(location, null, params => ({...params, testTab: stringifyId(tabId)})),
    [location],
  )

  const onSelectTab = useCallback(
    (tabId: string) => navigate(getTabLink(tabId)),
    [navigate, getTabLink],
  )

  useEffect(
    () =>
      projectInternalId != null
        ? subscribeOnProjectEvents(
            projectInternalId,
            [TEST_RESPONSIBILITY_CHANGED, TEST_MUTE_UPDATED],
            refetch,
          )
        : undefined,
    [projectInternalId, refetch],
  )

  const getTabsComponents = useCallback(
    (id: string) => {
      switch (id) {
        case TestPageTabNamesEnum.OVERVIEW:
          return testOccurrenceId != null ? (
            <TestOverviewTab
              key={`${stringifyId(projectId)}_${stringifyId(buildTypeId)}_${stringifyBranch(
                branch,
              )}`}
              testOccurrenceId={testOccurrenceId}
              testId={testId}
              projectId={projectId}
              buildTypeId={buildTypeId}
            />
          ) : null

        case TestPageTabNamesEnum.INVESTIGATION_HISTORY:
          return (
            <InvestigationHistory
              projectInternalId={projectInternalId}
              locator={`projectId=${stringifyId(projectId)}&testNameId=${stringifyId(testId)}`}
            />
          )

        default:
          return null
      }
    },
    [testOccurrenceId, testId, projectId, buildTypeId, projectInternalId, branch],
  )
  const renderTab = useCallback(
    (tab: TestTab) => {
      const tabId = tab.id
      const tabPage = getTabsComponents(tabId)
      return (
        <OverviewTab
          key={stringifyId(tabId)}
          id={stringifyId(tabId)}
          title={tab.title}
          href={getTabLink(tabId)}
        >
          {tabPage}
        </OverviewTab>
      )
    },
    [getTabLink, getTabsComponents],
  )
  const renderHeading = useMemo(() => {
    const formatedTestSuite = testPackage ? testSuite : trimAndRemoveColon(testSuite ?? '')

    const headingName =
      [testClass, testNameWithParameters].filter(Boolean).join('.') || testName || ''
    const subheadingName = [formatedTestSuite, testPackage].filter(Boolean).join('.') || ''

    return (
      <H1 className={styles.heading}>
        <span className={styles.headingTestName}>{`Test\u00a0History:\u00a0${headingName}`}</span>
        <CopyIconButton
          wrapperClassName={styles.copyTestNameIconWrapper}
          iconClassName={styles.copyTestNameIcon}
          text={fullTestName}
        />
        {subheadingName !== '' && <div className={styles.subHeading}>{subheadingName}</div>}
      </H1>
    )
  }, [testClass, testName, testNameWithParameters, fullTestName, testPackage, testSuite])

  if (!isReady) {
    return <Loader className={styles.pageLoader} />
  }

  if (!window.GLOBAL_ERROR_PAGE_DISABLED && (projectId == null || testOccurrenceId == null)) {
    const errorMessage =
      testOccurrenceId == null && projectId != null
        ? `Test with\u00a0id '${stringifyId(
            testId,
          )}' does\u00a0not\u00a0exist in\u00a0project\u00a0with\u00a0id '${stringifyId(
            projectId,
          )}'`
        : '404. Oops... wrong link'
    return <ErrorPage error={errorMessage} onRetry={refetch} />
  }

  return testOccurrenceId != null ? (
    <>
      <div className={styles.header}>
        {renderHeading}
        <Plugin placeId={PlaceIdList.SAKURA_BEFORE_CONTENT} />
        <ProjectsSelect buildTypeId={buildTypeId} projectId={projectId} testId={testId} />
        <div>
          <BranchFilter
            className={styles.branchFilter}
            projectOrBuildTypeNode={getProjectBuildTypeFilter({
              projectId,
              buildTypeId,
            })}
            onSelect={onSelectBranch}
            policy={Policy.HISTORY_BRANCHES}
          />
          <Suspense>
            <InvestigationMuteButtons
              className={styles.investigationMuteButtons}
              projectId={projectId}
              testOccurrencesIds={testOccurrenceIds}
              onAction={refetch}
            />
          </Suspense>
        </div>
      </div>
      <Tabs key={`${stringifyId(testId)}`} selected={currentTab} onSelect={onSelectTab}>
        {tabs.map(tab => renderTab(tab))}
      </Tabs>
    </>
  ) : null
}

export default memo<Props>(TestHistoryPage)
